import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { Dialog } from "@freightsimple/ui-core/Dialog/Dialog";
import { DialogResult } from "@freightsimple/ui-core/Dialog/DialogResult";
import type { ReactNode } from "react";
import { useRef, useState } from "react";

interface DeleteConfirmState {
  title: string;
  body: ReactNode;
}

/**
 * An awaitable confirm for the address-book delete flows.
 *
 * One dialog serves both the plain "delete this location?" and the louder
 * warning when a branch still has users assigned to it, which is why the body
 * is passed per call rather than fixed. A resolver ref bridges the declarative
 * dialog back to the boolean the calling flows await, and dismissing resolves
 * false — closing a delete confirm has to mean no.
 */
export function useLocationsDeleteConfirm() {
  const [deleteConfirmState, setDeleteConfirmState] = useState<
    DeleteConfirmState | undefined
  >(undefined);
  const deleteConfirmResolverRef = useRef<
    ((confirmed: boolean) => void) | undefined
  >(undefined);

  function askDeleteConfirm(title: string, body: ReactNode): Promise<boolean> {
    return new Promise((resolve) => {
      deleteConfirmResolverRef.current = resolve;
      setDeleteConfirmState({ title, body });
    });
  }

  function settleDeleteConfirm(confirmed: boolean) {
    deleteConfirmResolverRef.current?.(confirmed);
    deleteConfirmResolverRef.current = undefined;
  }

  const confirmDelete = () =>
    askDeleteConfirm(
      "Confirm delete",
      "Are you sure you want to delete this location?",
    );

  const dialogs = (
    <Dialog
      open={deleteConfirmState !== undefined}
      onOpenChange={function (nowOpen) {
        if (!nowOpen) {
          setDeleteConfirmState(undefined);
          settleDeleteConfirm(false);
        }
      }}
      title={deleteConfirmState?.title ?? ""}
      okText="Delete Location"
      onOk={function () {
        settleDeleteConfirm(true);
        return DialogResult.Close;
      }}
    >
      <ControlPresentationProvider presentation="underlined">
        {deleteConfirmState?.body}
      </ControlPresentationProvider>
    </Dialog>
  );

  return { askDeleteConfirm, confirmDelete, dialogs };
}
